"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type CellValue,
  groupColor,
  KmapDiagram,
  type MapGroup,
} from "@/components/tools/algebra/kmap-diagram";
import { QmView } from "@/components/tools/algebra/qm-view";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { groupBox, layoutFor } from "@/lib/tool-kit/algebra/kmap";
import {
  analyse,
  parseFormula,
  truthVector,
} from "@/lib/tool-kit/algebra/logic";
import {
  DEFAULT_NAMES,
  type LiteralStyle,
  type Minimization,
  minimize,
  productOfSums,
  productTerm,
  sumClause,
  sumOfProducts,
} from "@/lib/tool-kit/algebra/minimize";

/** English strings. A translated content file overrides these through `ui`. */
export const KARNAUGH_MAP_SOLVER_STRINGS = {
  variables: "Number of variables",
  variableOption: "{n} variables",
  mode: "Enter the function as",
  modes: {
    minterms: "Minterms and don't-cares",
    table: "Truth table",
    formula: "Formula",
  },
  mintermsLabel: "Minterms: the outputs that are 1",
  mintermsHint:
    "Whole numbers from 0 to {max}, separated by commas or spaces. A is the leading bit, so with three variables ABC = 101 is minterm 5.",
  dontCaresLabel: "Don't-cares (X): outputs you do not care about",
  tableHint:
    "Click an output to cycle through 0, 1 and X (don't care). Inputs are written A B C D with A as the leading bit.",
  outputButton: "Output for minterm {m}: {value}",
  inputsColumn: "Inputs",
  outputColumn: "Output",
  mintermColumn: "m",
  formulaLabel: "Formula",
  formulaHint:
    "For example A′B + AC, or (A ∨ B) ∧ ¬C. Use 2 to 4 different variables. The map is drawn with the variables in alphabetical order.",
  examplesLabel: "Try an example",
  exampleLabels: ["Majority", "Corners", "Don't-cares", "Needs Petrick"],
  exampleVariables: ["3", "4", "4", "3"],
  exampleOnes: [
    "3, 5, 6, 7",
    "0, 2, 8, 10",
    "1, 3, 7, 11, 15",
    "0, 1, 2, 5, 6, 7",
  ],
  exampleDontCares: ["", "", "0, 2, 5", ""],
  notation: "Write literals as",
  notations: { prime: "A′ (prime)", logic: "¬A" },
  results: "Minimal expressions",
  sop: "Minimal sum of products (SOP)",
  pos: "Minimal product of sums (POS)",
  literals: "Literals (SOP / POS)",
  literalsValue: "{sop} / {pos}",
  function: "Function",
  functionValue: "Σm({ones}){dc}",
  functionDc: " + d({dc})",
  none: "none",
  copySop: "Copy SOP",
  copyPos: "Copy POS",
  copied: "Copied!",
  constantZero:
    "The function is 0 for every input, so there is nothing to group.",
  constantOne:
    "The function is 1 for every input (counting don't-cares as 1), so one group covers the whole map.",
  sopMapHeading: "Map for the sum of products: group the 1s",
  posMapHeading: "Map for the product of sums: group the 0s",
  mapIntro:
    "Each outlined group is a block of 1, 2, 4, 8 or 16 cells whose variables that change drop out of the term. Groups may use X cells and may wrap round the edges.",
  mapTitle: "Karnaugh map for {kind} with the chosen groups outlined",
  kindSop: "the sum of products",
  kindPos: "the product of sums",
  legendTerm: "{term}",
  legendClause: "{term}",
  downloadPng: "Download map (PNG)",
  qmHeading: "Quine–McCluskey working",
  qmSopHeading: "For the sum of products (the 1s and X cells)",
  qmPosHeading: "For the product of sums (the 0s and X cells)",
  qmPosNote:
    "The product of sums is found by minimising the opposite function (where the output is 0) and then applying De Morgan's laws to each term.",
  qmStart: "Round {n}: every minterm as a pattern",
  qmRound:
    "Round {n}: patterns that differ in one place are merged (the changing place becomes a dash)",
  pattern: "Pattern",
  mintermsColumn: "Minterms",
  onesColumn: "Ones",
  status: "Status",
  merged: "merged",
  prime: "prime implicant",
  chartHeading: "Prime implicant chart",
  chartIntro:
    "A dot shows an implicant covering a required minterm. X cells help form groups but never need covering. A star marks an essential prime implicant.",
  primeColumn: "Implicant",
  termColumn: "Term",
  essentialColumn: "Essential",
  essentialLine:
    "Essential prime implicants: {list}. Each is the only implicant covering at least one required minterm, so every answer must include it.",
  noEssential:
    "There are no essential prime implicants: every required minterm can be covered in more than one way.",
  petrickHeading: "Petrick's method",
  petrickIntro:
    "Still uncovered after the essential implicants: {minterms}. Each must be covered by at least one of its implicants, which gives a product of sums:",
  petrickExpanded:
    "Multiplying out (and removing any product that contains a smaller one) leaves the covers:",
  petrickChoice:
    "The cheapest cover has the fewest terms and then the fewest literals: {choice}.",
  tieNote:
    "{count} covers tie for the minimum, so there are equally good answers: {list}. The first is shown.",
  coverLine: "Chosen cover: {cover}, with {literals} literals in all.",
  enter: "Enter the function to see its Karnaugh map.",
  errors: {
    invalid:
      "“{token}” is not a minterm number. Use whole numbers separated by commas or spaces.",
    range: "Minterms must be from 0 to {max}. Out of range: {list}.",
    formula: "The formula could not be read. Check the symbols and brackets.",
    variables:
      "A Karnaugh map needs 2 to 4 variables; this formula has {count}.",
  },
} as const;

type Strings = typeof KARNAUGH_MAP_SOLVER_STRINGS;
type Mode = keyof Strings["modes"];

const SEPARATORS = /[\s,;]+/;
const INTEGER = /^\d+$/;

interface Problem {
  n: number;
  names: string[];
  ones: number[];
  dc: number[];
}

type Reading = { ok: true; problem: Problem } | { ok: false; message: string };

function readList(
  text: string,
  max: number,
  strings: Strings
): number[] | string {
  const tokens = text.split(SEPARATORS).filter((token) => token !== "");
  const values: number[] = [];
  const outside: number[] = [];
  for (const token of tokens) {
    if (!INTEGER.test(token)) {
      return t(strings.errors.invalid, { token });
    }
    const value = Number(token);
    if (value > max) {
      outside.push(value);
    }
    values.push(value);
  }
  return outside.length > 0
    ? t(strings.errors.range, { max, list: outside.join(", ") })
    : [...new Set(values)].sort((a, b) => a - b);
}

function cellsToLists(
  cells: CellValue[],
  n: number
): { ones: number[]; dc: number[] } {
  const ones: number[] = [];
  const dc: number[] = [];
  for (let m = 0; m < 2 ** n; m += 1) {
    if (cells[m] === "1") {
      ones.push(m);
    } else if (cells[m] === "x") {
      dc.push(m);
    }
  }
  return { ones, dc };
}

function listsToCells(ones: number[], dc: number[]): CellValue[] {
  return Array.from({ length: 16 }, (_, m) => {
    if (ones.includes(m)) {
      return "1";
    }
    return dc.includes(m) ? "x" : "0";
  });
}

function readFormula(text: string, strings: Strings): Reading {
  const parsed = parseFormula(text);
  if (!parsed.ok) {
    return { ok: false, message: strings.errors.formula };
  }
  const count = parsed.variables.length;
  if (count < 2 || count > 4) {
    return { ok: false, message: t(strings.errors.variables, { count }) };
  }
  const analysis = analyse(truthVector(parsed.formula, parsed.variables));
  return {
    ok: true,
    problem: {
      n: count,
      names: parsed.variables,
      ones: analysis.minterms,
      dc: [],
    },
  };
}

function MapSection({
  heading,
  kind,
  min,
  values,
  names,
  literal,
  strings,
}: {
  heading: string;
  kind: string;
  min: Minimization;
  values: CellValue[];
  names: string[];
  literal: (index: number) => string;
  strings: Strings;
}) {
  const layout = layoutFor(min.n);
  const groups: MapGroup[] = min.cover.map((index) => ({
    box: groupBox(layout, min.primes[index]),
    label: `${index}`,
  }));
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{heading}</h3>
      <KmapDiagram
        downloadLabel={strings.downloadPng}
        fileName="karnaugh-map.png"
        groups={groups}
        layout={layout}
        names={names}
        title={t(strings.mapTitle, { kind })}
        values={values}
      />
      <ul className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-foreground text-sm">
        {min.cover.map((index, position) => (
          <li className="flex items-center gap-2" key={index}>
            <span
              aria-hidden="true"
              className="inline-block h-3 w-3 rounded"
              style={{ backgroundColor: groupColor(position) }}
            />
            {literal(index)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Results({
  problem,
  style,
  strings,
}: {
  problem: Problem;
  style: LiteralStyle;
  strings: Strings;
}) {
  const { n, names, ones, dc } = problem;
  const all = Array.from({ length: 2 ** n }, (_, m) => m);
  const zeros = all.filter((m) => !(ones.includes(m) || dc.includes(m)));
  const sopMin = minimize(n, ones, dc);
  const posMin = minimize(n, zeros, dc);
  const sop = sumOfProducts(sopMin, sopMin.cover, names, style);
  const pos = productOfSums(posMin, posMin.cover, names, style);
  const values: CellValue[] = all.map((m) => {
    if (ones.includes(m)) {
      return "1";
    }
    return dc.includes(m) ? "x" : "0";
  });
  const description = t(strings.functionValue, {
    ones: ones.join(", "),
    dc: dc.length > 0 ? t(strings.functionDc, { dc: dc.join(", ") }) : "",
  });
  const constant = sop === "0" || sop === "1";
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.sop} value={sop} />
        <StatCard emphasis label={strings.pos} value={pos} />
        <StatCard
          label={strings.literals}
          value={t(strings.literalsValue, {
            sop: sopMin.literals,
            pos: posMin.literals,
          })}
        />
        <StatCard label={strings.function} value={description} />
      </StatGrid>
      <div className="flex flex-wrap gap-2">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copySop}
          small
          text={sop}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyPos}
          small
          text={pos}
        />
      </div>
      {constant ? (
        <Notice>
          {sop === "0" ? strings.constantZero : strings.constantOne}
        </Notice>
      ) : null}
      <p className="text-muted-foreground text-sm">{strings.mapIntro}</p>
      <MapSection
        heading={strings.sopMapHeading}
        kind={strings.kindSop}
        literal={(index) => productTerm(sopMin.primes[index], n, names, style)}
        min={sopMin}
        names={names}
        strings={strings}
        values={values}
      />
      <MapSection
        heading={strings.posMapHeading}
        kind={strings.kindPos}
        literal={(index) => sumClause(posMin.primes[index], n, names, style)}
        min={posMin}
        names={names}
        strings={strings}
        values={values}
      />
      <h3 className={WIDGET_H3}>{strings.qmHeading}</h3>
      <p className="font-medium text-foreground text-sm">
        {strings.qmSopHeading}
      </p>
      <QmView
        min={sopMin}
        strings={strings}
        term={(index) => productTerm(sopMin.primes[index], n, names, style)}
      />
      <p className="font-medium text-foreground text-sm">
        {strings.qmPosHeading}
      </p>
      <p className="text-muted-foreground text-xs">{strings.qmPosNote}</p>
      <QmView
        min={posMin}
        strings={strings}
        term={(index) => sumClause(posMin.primes[index], n, names, style)}
      />
    </>
  );
}

export default function KarnaughMapSolverWidget() {
  const strings = useToolUi(KARNAUGH_MAP_SOLVER_STRINGS);
  const [n, setN] = useState(4);
  const [mode, setMode] = useState<Mode>("minterms");
  const [onesText, setOnesText] = useState<string>(strings.exampleOnes[2]);
  const [dcText, setDcText] = useState<string>(strings.exampleDontCares[2]);
  const [cells, setCells] = useState<CellValue[]>(
    listsToCells([1, 3, 7, 11, 15], [0, 2, 5])
  );
  const [formula, setFormula] = useState("A′B + AC");
  const [style, setStyle] = useState<LiteralStyle>("prime");
  const max = 2 ** n - 1;

  function current(): { ones: number[]; dc: number[] } | null {
    if (mode === "table") {
      return cellsToLists(cells, n);
    }
    const ones = readList(onesText, max, strings);
    const dc = readList(dcText, max, strings);
    return typeof ones === "string" || typeof dc === "string"
      ? null
      : { ones, dc };
  }

  function switchMode(next: Mode) {
    const lists = current();
    if (lists && next === "table") {
      setCells(listsToCells(lists.ones, lists.dc));
    } else if (lists && next === "minterms") {
      setOnesText(lists.ones.join(", "));
      setDcText(lists.dc.join(", "));
    }
    setMode(next);
  }

  function load(index: number) {
    setN(Number(strings.exampleVariables[index]));
    setOnesText(strings.exampleOnes[index]);
    setDcText(strings.exampleDontCares[index]);
    setMode("minterms");
  }

  let reading: Reading;
  if (mode === "formula") {
    reading = readFormula(formula, strings);
  } else if (mode === "table") {
    const lists = cellsToLists(cells, n);
    reading = {
      ok: true,
      problem: { n, names: DEFAULT_NAMES.slice(0, n), ...lists },
    };
  } else {
    const ones = readList(onesText, max, strings);
    const dc = readList(dcText, max, strings);
    if (typeof ones === "string") {
      reading = { ok: false, message: ones };
    } else if (typeof dc === "string") {
      reading = { ok: false, message: dc };
    } else {
      reading = {
        ok: true,
        problem: {
          n,
          names: DEFAULT_NAMES.slice(0, n),
          ones,
          dc: dc.filter((m) => !ones.includes(m)),
        },
      };
    }
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "formula" ? null : (
          <SelectField
            id="kmap-n"
            label={strings.variables}
            onChange={(value) => setN(Number(value))}
            options={["2", "3", "4"].map((value) => ({
              value,
              label: t(strings.variableOption, { n: value }),
            }))}
            value={String(n)}
          />
        )}
        <PillGroup
          label={strings.mode}
          name="kmap-mode"
          onChange={switchMode}
          options={(["minterms", "table", "formula"] as const).map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
      </div>
      {mode === "minterms" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={t(strings.mintermsHint, { max })}
            id="kmap-ones"
            label={strings.mintermsLabel}
            mono
            onChange={setOnesText}
            value={onesText}
          />
          <TextField
            id="kmap-dc"
            label={strings.dontCaresLabel}
            mono
            onChange={setDcText}
            value={dcText}
          />
        </div>
      ) : null}
      {mode === "formula" ? (
        <TextField
          hint={strings.formulaHint}
          id="kmap-formula"
          label={strings.formulaLabel}
          mono
          onChange={setFormula}
          value={formula}
        />
      ) : null}
      {mode === "table" ? (
        <div className="space-y-2">
          <p className="text-muted-foreground text-xs">{strings.tableHint}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {Array.from({ length: 2 ** n }, (_, m) => (
              <div
                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
                // biome-ignore lint/suspicious/noArrayIndexKey: m is the minterm number
                key={m}
              >
                <span className="font-mono text-muted-foreground text-xs">
                  {m} · {m.toString(2).padStart(n, "0")}
                </span>
                <button
                  aria-label={t(strings.outputButton, {
                    m,
                    value: (cells[m] ?? "0").toUpperCase(),
                  })}
                  className={`${BUTTON_SMALL} w-10 font-mono`}
                  onClick={() => {
                    const order: CellValue[] = ["0", "1", "x"];
                    const next =
                      order[(order.indexOf(cells[m] ?? "0") + 1) % 3];
                    setCells(cells.map((value, i) => (i === m ? next : value)));
                  }}
                  type="button"
                >
                  {(cells[m] ?? "0").toUpperCase()}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">
          {strings.examplesLabel}
        </span>
        {strings.exampleLabels.map((label, i) => (
          <button
            className={BUTTON_SMALL}
            key={label}
            onClick={() => load(i)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>
      <PillGroup
        label={strings.notation}
        name="kmap-style"
        onChange={setStyle}
        options={(["prime", "logic"] as const).map((value) => ({
          value,
          label: strings.notations[value],
        }))}
        value={style}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {reading.ok ? (
          <Results problem={reading.problem} strings={strings} style={style} />
        ) : (
          <ErrorNotice>{reading.message}</ErrorNotice>
        )}
      </section>
    </>
  );
}
